import { z } from 'zod';

import {
  hasItemsSelected,
  type WorkflowData,
  type StepBaseConfig,
  type StepConfig,
  type Steps,
  type TaskConfig,
  type Tasks,
  type TaskSchemaConfig,
} from './schemaTypes';
import { getEffectiveMode } from './taskMode';
import type { TaskRuntimeContext } from './types';
import { ItemSelectionTaskSchema } from '../components/tasks/ItemSelectionTask/ItemSelectionTaskSchema';
import { MethodTaskSchema } from '../components/tasks/MethodTask/MethodTaskSchema';
import { ReasonsTaskSchema } from '../components/tasks/ReasonsTask/ReasonsTaskSchema';
import { RefundTaskSchema } from '../components/tasks/RefundTask/RefundTaskSchema';
import { ReviewItemSummaryTaskSchema } from '../components/tasks/ReviewItemSummaryTask/ReviewItemSummaryTaskSchema';
import { ReturnPolicyTaskSchema } from '../components/tasks/ReturnPolicyTask/ReturnPolicyTaskSchema';
import { ResolutionTaskSchema } from '../components/tasks/ResolutionTask/ResolutionTaskSchema';
import { ReviewRefundSummaryTaskSchema } from '../components/tasks/ReviewRefundSummaryTask/ReviewRefundSummaryTaskSchema';
import { SignatureTaskSchema } from '../components/tasks/SignatureTask/SignatureTaskSchema';
import { OrderlessCustomerFormSchema } from '../components/tasks/OrderlessTask/OrderlessTaskSchema';
import { GiftEmailTaskSchema } from '../components/tasks/GiftEmailTask/GiftEmailTaskSchema';

// Task Configurations

const ItemSelectionTask: TaskSchemaConfig = {
  id: 'itemSelection',
  schema: ItemSelectionTaskSchema,
};

const MethodTask: TaskSchemaConfig = {
  id: 'returnMethod',
  schema: MethodTaskSchema,
};

const ReasonsTask: TaskSchemaConfig = {
  id: 'reason',
  schema: ReasonsTaskSchema,
};

const ResolutionsTask: TaskSchemaConfig = {
  id: 'resolution',
  schema: ResolutionTaskSchema,
};

const RefundTask: TaskSchemaConfig = {
  id: 'refund',
  schema: RefundTaskSchema,
};

const ReturnPolicyTask: TaskSchemaConfig = {
  id: 'returnPolicy',
  schema: ReturnPolicyTaskSchema,
};

const SignatureTask: TaskSchemaConfig = {
  id: 'signature',
  schema: SignatureTaskSchema,
};

const ReviewItemSummaryTask: TaskSchemaConfig = {
  id: 'reviewItemSummary',
  schema: ReviewItemSummaryTaskSchema,
};

const ReviewRefundSummaryTask: TaskSchemaConfig = {
  id: 'reviewRefundSummary',
  schema: ReviewRefundSummaryTaskSchema,
};

const OrderlessCustomerTask: TaskSchemaConfig = {
  id: 'orderlessCustomer',
  schema: OrderlessCustomerFormSchema,
};

const GiftEmailTaskConfig: TaskSchemaConfig = {
  id: 'giftEmail',
  schema: GiftEmailTaskSchema,
};

// Step Configurations

const ItemSelectionStep: StepBaseConfig = {
  id: 'itemSelection',
  route: '/$retailer/item-selection',
};

const ReasonsStep: StepBaseConfig = {
  id: 'reasons',
  route: '/$retailer/reasons',
};

const MethodStep: StepBaseConfig = {
  id: 'returnMethod',
  route: '/$retailer/method',
};

const ReviewStep: StepBaseConfig = {
  id: 'review',
  route: '/$retailer/review',
};

const OrderlessStep: StepBaseConfig = {
  id: 'orderless',
  route: '/$retailer/orderless',
};

// Workflow Maps

export const workflowTaskMap: Record<Tasks, TaskSchemaConfig> = {
  itemSelection: ItemSelectionTask,
  reason: ReasonsTask,
  resolution: ResolutionsTask,
  returnMethod: MethodTask,
  refund: RefundTask,
  reviewItemSummary: ReviewItemSummaryTask,
  reviewRefundSummary: ReviewRefundSummaryTask,
  returnPolicy: ReturnPolicyTask,
  signature: SignatureTask,
  orderlessCustomer: OrderlessCustomerTask,
  giftEmail: GiftEmailTaskConfig,
} as const;

export const workflowStepMap: Record<Steps, StepBaseConfig> = {
  itemSelection: ItemSelectionStep,
  reasons: ReasonsStep,
  returnMethod: MethodStep,
  review: ReviewStep,
  orderless: OrderlessStep,
} as const;

// Validation

export const validateStep = (
  step: StepConfig,
  data: WorkflowData | null,
  taskRuntimeContext?: TaskRuntimeContext,
): Record<string, z.ZodError | null> => {
  const errors: Record<string, z.ZodError | null> = {};

  // Validate only enabled, explicit tasks
  const tasksToValidate = step.tasks.filter((task) => {
    const mode = getEffectiveMode(task, taskRuntimeContext);
    return mode !== 'disabled' && mode !== 'implicit';
  });

  for (const task of tasksToValidate) {
    try {
      if (!hasItemsSelected(data)) {
        throw new Error('No items selected');
      }

      if (task.scope === 'cart' && data && task.id in data) {
        task.schema.parse(data[task.id as keyof WorkflowData]);
      } else {
        if (!data?.items) {
          throw new Error('No items in data');
        }
        // for all items selected, parse the schema
        for (const itemId in data.items) {
          const taskData =
            data?.items?.[itemId] && task.id in data.items[itemId]
              ? (data.items[itemId] as Record<Tasks, unknown>)[task.id]
              : null;

          task.schema.parse(taskData);
        }
      }
      errors[task.id] = null;
    } catch (error) {
      if (error instanceof z.ZodError) {
        errors[task.id] = error;
      }
    }
  }

  return errors;
};

export const isStepValid = (
  errors: Record<string, z.ZodError | null>,
  step: StepConfig,
  taskRuntimeContext?: TaskRuntimeContext,
): boolean =>
  step.tasks.every((task) => {
    const mode = getEffectiveMode(task, taskRuntimeContext);
    if (mode === 'disabled' || mode === 'implicit') return true;
    return errors[task.id] == null; // null or undefined
  });

export const createSchemaFromTasks = (
  tasks: TaskConfig[],
  existingData?: WorkflowData | null,
  taskRuntimeContext?: TaskRuntimeContext,
) => {
  const itemSchemaShape = tasks
    // Exclude disabled and implicit tasks from the current step's form schema
    .filter((task) => {
      const mode = getEffectiveMode(task, taskRuntimeContext);
      return (
        task.scope === 'item' && mode !== 'disabled' && mode !== 'implicit'
      );
    })
    .reduce<Record<string, z.ZodType>>((acc, task) => {
      acc[task.id] = task.schema;
      return acc;
    }, {});

  // Preserve itemSelection data if it exists but isn't in current step
  if (existingData?.items && !itemSchemaShape.itemSelection) {
    const hasItemSelectionData = Object.values(existingData.items).some(
      (itemData) => itemData.itemSelection,
    );
    if (hasItemSelectionData) {
      itemSchemaShape.itemSelection = ItemSelectionTaskSchema;
    }
  }

  const cartSchemaShape = tasks
    // Exclude disabled and implicit tasks from the current step's form schema
    .filter((task) => {
      const mode = getEffectiveMode(task, taskRuntimeContext);
      return (
        task.scope === 'cart' && mode !== 'disabled' && mode !== 'implicit'
      );
    })
    .reduce<Record<string, z.ZodType>>((acc, task) => {
      acc[task.id] = task.schema;
      return acc;
    }, {});

  // Create the base schema with cart items
  const baseSchema = { ...cartSchemaShape };

  // Only include the items field if there are item tasks
  if (Object.keys(itemSchemaShape).length > 0) {
    baseSchema.items = z.record(z.string(), z.object(itemSchemaShape));
  }

  return z.object(baseSchema);
};
